import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { UserProfile } from '../../lib/api';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { UserDetailsModal } from './UserDetailsModal';
import {
  ChangeRoleDialog,
  ChangeCompanyDialog,
  ActivityLogDialog,
  BulkApproveDialog,
  BulkSendInvitationsDialog,
} from './UserManagementDialogs';
import UserTable from './UserTable';
import UsersFilterBar from './UsersFilterBar';
import DeleteUserDialog from './DeleteUserDialog';
import { useUserActions } from './useUserActions';
import { useUsersList } from './useUsersList';
import type { UserTypeTab } from './usersFilters';
import { exportUsersToCSV } from './usersExport';
import {
  Search, Users as UsersIcon, Download, Mail,
  Building2, UserPlus, Truck, UserCheck, CheckCircle,
  AlertCircle, RefreshCw, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

export default function UsersManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, startImpersonation } = useAuth();
  const { viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId } = useView();
  const actions = useUserActions();

  // Selection & Tabs
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  // CR-5367: third tab for Valuechain (internal Helix) users so the
  // Customer Users tab no longer doubles as a catch-all for everything
  // non-supplier — Helix users now have their own bucket.
  // CR-5367 rework: supplier admins land on the supplier tab so they
  // see their own colleagues by default; everyone else lands on
  // customer (the historical default).
  const supplierUserDefault =
    (user?.company_type === 'supplier' || user?.platform === 'SUPPLIER');
  // ?tab= restores the tab on return from the Add User wizard, so adding a
  // supplier user lands back on Supplier Users rather than the default tab.
  const tabParam = searchParams.get('tab');
  const [userTypeTab, setUserTypeTab] = useState<UserTypeTab>(
    supplierUserDefault ? 'supplier' : tabParam === 'supplier' ? 'supplier' : 'customer'
  );

  // CR-5312: confirm-delete dialog state.
  const [deleteCandidate, setDeleteCandidate] = useState<UserProfile | null>(null);

  // Permissions
  /** Only Django superusers may grant/revoke target users' is_superuser/is_staff (modal switch). */
  const isSuperAdmin = Boolean(user?.is_superuser);
  // CR-5367 rework: Valuechain Users tab is internal Helix-only. Gate
  // strictly on the home company (platform/company_type). Previously
  // accepted is_superuser as a fallback, which leaked the tab to any
  // customer / supplier account that happened to have is_superuser set
  // on it via test data.
  const isHelixViewer = user?.platform === 'HELIX' || user?.company_type === 'helix';
  // Relinking a user to another company is a platform repair tool —
  // helix staff / superusers only (backend enforces the same gate).
  const canRelinkCompany = isHelixViewer || Boolean(user?.is_superuser) || Boolean(user?.is_staff);
  const isSupplierViewer = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  // CR-5367 rework: user.role is a string label (e.g. "Customer Admin")
  // — the prior cast to Record<string, boolean> always returned
  // undefined and silently denied customer/supplier admins this page.
  // The top-level is_company_admin / can_manage_users fields are the
  // authoritative source.
  const canManageUsers = isHelixViewer || user?.is_staff || user?.is_superuser ||
    Boolean(user?.is_company_admin) || Boolean(user?.can_manage_users);

  // CR-6273: who may use "Login As" (now reachable only from the user-detail
  // view). VC-internal admins, or a customer admin — never a supplier user.
  // Mirrors the backend ImpersonateView eligibility check exactly: Helix
  // company type, Django superuser, or a (non-supplier) customer admin.
  // PR #630 review: is_staff alone is NOT accepted by the backend, so it
  // must not gate the button here — otherwise a non-Helix staff account
  // would see a "Login As" button that always 403s on click.
  const canImpersonate =
    isHelixViewer || Boolean(user?.is_superuser) ||
    ((Boolean(user?.is_company_admin) || Boolean(user?.can_manage_users)) && !isSupplierViewer);

  // CR-6273: impersonate the inspected user. The list is already scoped to the
  // active company/site view, and Helix targets are blocked here and on the
  // server, so this stays within the admin's hierarchy. Passing the user's
  // company as view_company_id pins the impersonated session to that tenant.
  const handleLoginAs = async (userProfile: UserProfile) => {
    if (userProfile.company_type === 'helix') return;
    try {
      const response = await authAPI.impersonate(
        userProfile.user.id, undefined, userProfile.company,
      );
      const data = response.data.data;
      startImpersonation(
        data.target_user,
        data.target_token,
        data.original_token,
        undefined,
        data.audit_log_id,
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        axiosErr.response?.data?.message
        || axiosErr.response?.data?.error
        || 'Failed to start Login As session',
      );
    }
  };

  /** Re-fetch the list at the active view scope. */
  const refreshUsers = () => actions.fetchUsers({
    view_level: viewLevel,
    group: selectedGroupId && selectedGroupId !== 0 ? selectedGroupId : null,
    company: selectedCompanyId ?? null,
    site: selectedSiteId ?? null,
  });

  useEffect(() => {
    if (!canManageUsers) { navigate('/dashboard'); return; }
    // CR-5374: re-fetch when the view-context scope changes so the
    // Customer / Supplier tab cohorts narrow to the active Group /
    // Division / Site. selectedGroupId === 0 is the synthetic
    // "All Companies" rollup — treat as no scope so the backend
    // returns the platform-wide list.
    actions.fetchUsers({
      view_level: viewLevel,
      group: selectedGroupId && selectedGroupId !== 0 ? selectedGroupId : null,
      company: selectedCompanyId ?? null,
      site: selectedSiteId ?? null,
    });
  }, [canManageUsers, navigate, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId]);

  // Filters, sorting and client-side paging over the fetched list.
  const { users } = actions;
  const list = useUsersList({ users, userTypeTab, isHelixViewer });
  const { filteredAndSortedUsers, paginatedUsers } = list;
  const pendingInSelectionCount = useMemo(() => {
    return users.filter((u) => selectedUsers.has(u.id) && !u.is_approved).length;
  }, [users, selectedUsers]);

  // --- Handlers ---

  const isSelf = (userProfile: { user: { id: number } }) => userProfile.user.id === user?.id;

  const toggleUserSelection = (userId: number) => {
    const next = new Set(selectedUsers);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setSelectedUsers(next);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedUsers.map((u) => u.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedUsers.has(id));
    const next = new Set(selectedUsers);
    if (allPageSelected) {
      pageIds.forEach((id) => next.delete(id));
    } else {
      pageIds.forEach((id) => next.add(id));
    }
    setSelectedUsers(next);
  };

  const selectAllNotInvited = () => {
    const ids = filteredAndSortedUsers
      .filter(u => !u.user.has_joined && !u.user.invitation_sent).map(u => u.id);
    setSelectedUsers(new Set(ids));
  };

  const exportToCSV = () => exportUsersToCSV(filteredAndSortedUsers);

  const handleBulkApproveWrapped = async () => {
    await actions.handleBulkApprove(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  const handleApproveUserWrapped = async (userId: number) => {
    await actions.handleApproveUser(userId);
  };

  const handleBulkSendInvitationsWrapped = async () => {
    await actions.handleBulkSendInvitations(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  if (!canManageUsers) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <UsersIcon className="h-8 w-8" />Users Management
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={refreshUsers}
            variant="outline"
            size="icon"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />Export CSV
          </Button>
        </div>
      </div>

      {actions.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" /><AlertDescription>{actions.error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg"><UsersIcon className="h-5 w-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{filteredAndSortedUsers.length}</p>
          <p className="text-sm text-muted-foreground">Total Users from {list.uniqueCompanyCount} companies</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg"><UserCheck className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold text-green-600">{list.joinedCount}</p>
          <p className="text-sm text-muted-foreground">Users Joined ({list.joinedPercent}% of total)</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg"><Mail className="h-5 w-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-600">{list.notJoinedCount}</p>
          <p className="text-sm text-muted-foreground">Not Joined ({list.invitedSentCount} invitations sent)</p></div>
        </div></CardContent></Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {/* The wizard invites customer or supplier users only, so the button
              names the type it will open and is hidden on the Valuechain tab. */}
          {userTypeTab !== 'valuechain' && (
            <Button onClick={() => navigate(`/users/invite?type=${userTypeTab}`)} className="gap-2">
              <UserPlus className="h-4 w-4" />Add {userTypeTab === 'supplier' ? 'Supplier' : 'Customer'} User
            </Button>
          )}
          {selectedUsers.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkApproveDialog(true)}
                disabled={actions.actionLoading || pendingInSelectionCount === 0}
                className="gap-2"
                title={pendingInSelectionCount === 0 ? 'No pending users in selection' : undefined}
              >
                <CheckCircle className="h-4 w-4" />
                Approve pending ({pendingInSelectionCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkInviteDialog(true)}
                disabled={actions.actionLoading}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                Send invitations ({selectedUsers.size})
              </Button>
              <Button variant="outline" onClick={() => setSelectedUsers(new Set())}>Clear Selection</Button>
            </>
          )}
          {list.notInvitedCount > 0 && (
            <Button variant="outline" onClick={selectAllNotInvited} className="gap-2">
              <Mail className="h-4 w-4" />Select All Not Invited ({list.notInvitedCount})
            </Button>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={list.searchTerm} onChange={(e) => list.setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </div>
      </div>

      {/* Additional Filters */}
      <UsersFilterBar list={list} />

      {/* CR-5367: Customer / Supplier / Valuechain Tabs.
          CR-5374: Valuechain tab hidden from external (non-Helix) viewers.
          CR-5367 rework: supplier admins only manage their own company —
          the Customer / Valuechain tabs are noise (and a perceived
          cross-tenant leak), so suppliers see no tab strip and the
          list shows just their supplier-side colleagues. */}
      {!isSupplierViewer && (
        <Tabs value={userTypeTab} onValueChange={(v) => { setUserTypeTab(v as UserTypeTab); list.setCurrentPage(1); setSearchParams({ tab: v }, { replace: true }); }}>
          <TabsList>
            <TabsTrigger value="customer" className="gap-2">
              <Building2 className="h-4 w-4" />Customer Users<Badge variant="secondary" className="ml-1">{list.customerCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="supplier" className="gap-2">
              <Truck className="h-4 w-4" />Supplier Users<Badge variant="secondary" className="ml-1">{list.supplierCount}</Badge>
            </TabsTrigger>
            {isHelixViewer && (
              <TabsTrigger value="valuechain" className="gap-2">
                <Shield className="h-4 w-4" />Valuechain Users<Badge variant="secondary" className="ml-1">{list.valuechainCount}</Badge>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      )}

      {/* Table */}
      <UserTable
        users={paginatedUsers} selectedUsers={selectedUsers}
        emptyMessage={list.hasActiveFilters && {
          customer: list.customerCount, supplier: list.supplierCount, valuechain: list.valuechainCount,
        }[userTypeTab] > 0 ? 'No users match these filters' : undefined}
        onToggleSelection={toggleUserSelection} onToggleSelectAll={toggleSelectAll}
        onViewDetails={actions.openUserDetails} onToggleActive={actions.handleToggleActive}
        onChangeRole={actions.openChangeRoleDialog} onSendInvitation={actions.handleSendInvitation}
        onChangeCompany={canRelinkCompany ? actions.openChangeCompanyDialog : undefined}
        onApprove={handleApproveUserWrapped}
        onSafeDelete={(u) => setDeleteCandidate(u)}
        onViewActivity={actions.handleViewActivityLog}
        canImpersonate={canImpersonate} onLoginAs={handleLoginAs}
        isSelf={isSelf} loading={actions.loading} actionLoading={actions.actionLoading}
        sortField={list.sortField} sortDirection={list.sortDirection} onSort={list.handleSort}
        currentPage={list.currentPage} totalPages={list.totalPages}
        totalFiltered={filteredAndSortedUsers.length} onPageChange={list.setCurrentPage}
      />

      <DeleteUserDialog
        candidate={deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={actions.handleSafeDelete}
        actionLoading={actions.actionLoading}
      />

      {/* Dialogs */}
      <UserDetailsModal
        open={actions.showUserDetails} onOpenChange={actions.setShowUserDetails}
        selectedUser={actions.selectedUser} userDetailTab={actions.userDetailTab}
        setUserDetailTab={actions.setUserDetailTab}
        userSitePermissions={actions.userSitePermissions}
        loadingSitePermissions={actions.loadingSitePermissions}
        savingPermission={actions.savingPermission}
        savingDivision={actions.savingDivision}
        isSuperAdmin={isSuperAdmin || false} actionLoading={actions.actionLoading}
        isSelf={isSelf} handleToggleActive={actions.handleToggleActive}
        openChangeRoleDialog={actions.openChangeRoleDialog}
        openChangeCompanyDialog={canRelinkCompany ? actions.openChangeCompanyDialog : undefined}
        handleToggleAdmin={actions.handleToggleAdmin}
        handleToggleSiteLink={actions.handleToggleSiteLink}
        handleToggleSitePermission={actions.handleToggleSitePermission}
        handleAssignDivision={actions.handleAssignDivision}
        handleApproveUser={handleApproveUserWrapped}
        handleRejectUser={actions.handleRejectUser}
        handleSendInvitation={actions.handleSendInvitation}
        canImpersonate={canImpersonate}
        handleLoginAs={handleLoginAs}
        onPrimaryContactsSaved={refreshUsers}
        onDepartmentSaved={(profileId, department) => actions.patchUser(profileId, { department })}
      />
      <BulkApproveDialog
        open={actions.showBulkApproveDialog} onOpenChange={actions.setShowBulkApproveDialog}
        selectedCount={selectedUsers.size}
        pendingInSelectionCount={pendingInSelectionCount}
        actionLoading={actions.actionLoading}
        onBulkApprove={handleBulkApproveWrapped}
      />
      <BulkSendInvitationsDialog
        open={actions.showBulkInviteDialog}
        onOpenChange={actions.setShowBulkInviteDialog}
        selectedCount={selectedUsers.size}
        actionLoading={actions.actionLoading}
        onConfirm={handleBulkSendInvitationsWrapped}
      />
      <ChangeCompanyDialog
        open={actions.showChangeCompanyDialog} onOpenChange={actions.setShowChangeCompanyDialog}
        selectedUser={actions.selectedUser} actionLoading={actions.actionLoading}
        onRelink={actions.handleRelinkCompany}
      />
      <ChangeRoleDialog
        open={actions.showChangeRoleDialog} onOpenChange={actions.setShowChangeRoleDialog}
        selectedUser={actions.selectedUser} availableRoles={actions.availableRoles}
        selectedRoleId={actions.selectedRoleId} setSelectedRoleId={actions.setSelectedRoleId}
        actionLoading={actions.actionLoading} onChangeRole={actions.handleChangeRole}
        onChangeSupplierRole={actions.handleChangeSupplierRole}
        canHandleAdminRoles={isHelixViewer || Boolean(user?.is_company_admin)}
      />
      <ActivityLogDialog
        open={actions.showActivityLogDialog} onOpenChange={actions.setShowActivityLogDialog}
        selectedUser={actions.selectedUser} activityLogs={actions.activityLogs}
      />
    </div>
  );
}
